import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { tasks as tasksApi } from '../../lib/api';
import { formatApiDateTime } from '../../lib/format';
import {
  AlertCircle,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  Clock3,
  Loader2,
  MessageSquare,
  RefreshCw,
  XCircle,
} from 'lucide-react';

const STATUS_TABS = [
  { key: 'active', label: 'Open' },
  { key: 'done', label: 'Done' },
  { key: 'dismissed', label: 'Dismissed' },
  { key: 'all', label: 'All' },
];

const KIND_TABS = [
  { key: 'all', label: 'All' },
  { key: 'quote', label: 'Quotes' },
  { key: 'meeting', label: 'Meetings' },
  { key: 'missing_info', label: 'Missing info' },
  { key: 'follow_up', label: 'Follow-ups' },
];

const KIND_LABELS = {
  quote: 'Quote',
  meeting: 'Meeting',
  missing_info: 'Missing info',
  follow_up: 'Follow-up',
  custom: 'Custom',
};

const PRIORITY_CLASSES = {
  high: 'bg-[#FFEBE9] text-[#CE3636] border-[#FF8080]',
  normal: 'bg-[#F7F8F6] text-[#6B7280] border-[#D7DCE4]',
  low: 'bg-[#E6FAF9] text-[#0B1D3A] border-[#9CE7E6]',
};

const APPROVAL_CLASSES = {
  pending: 'bg-[#FFF7E6] text-[#B35900] border-[#F4D8A7]',
  approved: 'bg-[#EEFBDD] text-[#376B2B] border-[#A8E89C]',
  rejected: 'bg-[#FFEBE9] text-[#CE3636] border-[#FF8080]',
};

export default function Tasks() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');
  const [kindFilter, setKindFilter] = useState('all');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await tasksApi.list({ status: statusFilter, kind: kindFilter, limit: 200 });
      setItems(data.items || []);
    } catch (err) {
      setError(err.message || 'Could not load tasks.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [kindFilter, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const groupedCount = useMemo(() => ({
    total: items.length,
    waiting: items.filter((item) => item.status === 'waiting').length,
  }), [items]);

  const updateTask = async (taskId, patch) => {
    try {
      setBusyId(taskId);
      await tasksApi.update(taskId, patch);
      window.dispatchEvent(new CustomEvent('linirio:tasks-changed'));
      await load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="app-page-shell bg-[#F7F8F6]" data-testid="tasks-page">
      <header className="h-14 border-b border-[#D7DCE4] flex items-center justify-between px-4 sm:px-6 sticky top-0 bg-[#F7F8F6]/95 backdrop-blur z-10">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-5 h-5 text-[#6B7280]" />
          <h1 className="text-lg font-semibold text-[#0B1D3A]">Tasks</h1>
          <span className="text-xs text-[#6B7280]">{groupedCount.total} items</span>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#D7DCE4] rounded-xl text-sm text-[#0B1D3A] hover:bg-[#E6FAF9]"
          data-testid="tasks-refresh-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </header>

      <div className="app-page-scroll">
        <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-5">
        {error ? (
          <div className="flex items-start gap-2 rounded-lg border border-[#FF8080] bg-[#FFEBE9] px-3 py-2 text-sm text-[#CE3636]">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            {error}
          </div>
        ) : null}

        <section className="border border-[#D7DCE4] rounded-2xl p-4 bg-white shadow-[0_18px_34px_-28px_rgba(11,29,58,0.22)] space-y-3">
          <div className="flex flex-wrap gap-2">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-xl border ${statusFilter === tab.key ? 'bg-[#12C8C6] text-white border-[#12C8C6]' : 'bg-white text-[#6B7280] border-[#D7DCE4]'}`}
                data-testid={`tasks-status-${tab.key}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {KIND_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setKindFilter(tab.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-xl border ${kindFilter === tab.key ? 'bg-[#12C8C6] text-white border-[#12C8C6]' : 'bg-white text-[#6B7280] border-[#D7DCE4]'}`}
                data-testid={`tasks-kind-${tab.key}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-[#6B6B6B]">
            Waiting approvals: {groupedCount.waiting}. Escalated AI promises such as quotes, meetings, and promised follow-ups stay here until someone finishes the next step.
          </p>
        </section>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-[#6B6B6B]" />
          </div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-[#D7DCE4] rounded-2xl py-16 px-6 text-center bg-white">
            <ClipboardList className="w-8 h-8 mx-auto text-[#6B7280]" />
            <h2 className="mt-3 text-base font-semibold text-[#0B1D3A]">No tasks in this view</h2>
            <p className="mt-1 text-sm text-[#6B7280]">AI-created operational work will appear here when a conversation needs a quote, meeting, missing information, or a manual follow-up.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((task) => {
              const priorityClass = PRIORITY_CLASSES[task.priority] || PRIORITY_CLASSES.normal;
              const approvalClass = task.approval_status ? (APPROVAL_CLASSES[task.approval_status] || APPROVAL_CLASSES.pending) : '';
              return (
                <article key={task.id} className="border border-[#D7DCE4] rounded-2xl bg-white p-4 shadow-[0_18px_34px_-28px_rgba(11,29,58,0.22)]" data-testid={`task-${task.id}`}>
                  <div className="flex items-start gap-3">
                    {task.lead_picture ? (
                      <img src={task.lead_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#F0F0EF] flex items-center justify-center text-sm font-medium text-[#6B6B6B]">
                        {(task.lead_name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-sm font-semibold text-[#191919]">{task.title}</h2>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-medium ${priorityClass}`}>{task.priority || 'normal'}</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-medium bg-[#FBFBFA] text-[#191919] border-[#E3E2E0]">
                          {KIND_LABELS[task.kind] || task.kind}
                        </span>
                        {task.approval_status ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-medium ${approvalClass}`}>
                            Approval {task.approval_status}
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-1 text-xs text-[#6B6B6B] flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span>{task.lead_name || 'Unknown contact'}</span>
                        <span className="inline-flex items-center gap-1"><Clock3 className="w-3 h-3" /> {task.updated_at ? formatApiDateTime(task.updated_at) : ''}</span>
                      </div>
                      <p className="mt-3 text-sm text-[#191919]">{task.description}</p>
                      {task.ai_reason ? (
                        <p className="mt-2 text-xs text-[#6B6B6B]"><span className="font-medium text-[#191919]">AI reason:</span> {task.ai_reason}</p>
                      ) : null}
                      {task.due_hint ? (
                        <p className="mt-1 text-xs text-[#6B6B6B]"><span className="font-medium text-[#191919]">Next step:</span> {task.due_hint}</p>
                      ) : null}
                      {task.suggested_reply ? (
                        <div className="mt-3 rounded-lg border border-[#E3E2E0] bg-[#FBFBFA] p-3">
                          <p className="text-[11px] font-semibold text-[#6B6B6B] mb-1">Suggested reply</p>
                          <p className="text-sm text-[#191919] whitespace-pre-wrap">{task.suggested_reply}</p>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      onClick={() => navigate(`/app?conversation_id=${task.conversation_id}`)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#D7DCE4] rounded-xl text-sm text-[#0B1D3A] hover:bg-[#E6FAF9]"
                    >
                      <MessageSquare className="w-4 h-4" />
                      Open chat
                    </button>
                    {task.kind === 'meeting' ? (
                      <button
                        onClick={() => navigate(`/app/calendar?task_id=${task.id}`)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#D7DCE4] rounded-xl text-sm text-[#0B1D3A] hover:bg-[#E6FAF9]"
                      >
                        <CalendarDays className="w-4 h-4" />
                        {task.calendar_connected ? 'Open calendar' : 'Connect calendar'}
                      </button>
                    ) : null}
                    {task.approval_status === 'pending' ? (
                      <button
                        onClick={() => navigate('/app/approvals')}
                        className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#D7DCE4] rounded-xl text-sm text-[#0B1D3A] hover:bg-[#E6FAF9]"
                      >
                        <ArrowRight className="w-4 h-4" />
                        Open approvals
                      </button>
                    ) : null}
                    {task.status !== 'in_progress' ? (
                      <button
                        onClick={() => updateTask(task.id, { status: 'in_progress' })}
                        disabled={busyId === task.id}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#E6FAF9] rounded-xl text-sm text-[#0B1D3A] hover:bg-[#D5F4F3] disabled:opacity-50"
                      >
                        {busyId === task.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock3 className="w-4 h-4" />}
                        Mark in progress
                      </button>
                    ) : null}
                    {task.status !== 'done' ? (
                      <button
                        onClick={() => updateTask(task.id, { status: 'done' })}
                        disabled={busyId === task.id}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#12C8C6] text-white rounded-xl text-sm hover:bg-[#0FB3B1] disabled:opacity-50"
                      >
                        {busyId === task.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                        Mark done
                      </button>
                    ) : null}
                    {task.status !== 'dismissed' ? (
                      <button
                        onClick={() => updateTask(task.id, { status: 'dismissed' })}
                        disabled={busyId === task.id}
                        className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#FF8080] text-[#CE3636] rounded-xl text-sm hover:bg-[#FFF5F5] disabled:opacity-50"
                      >
                        <XCircle className="w-4 h-4" />
                        Dismiss
                      </button>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
